import {useEffect} from 'react';
import {usePageMetadata} from 'mkt-ssr/lib/page/metadata';
import {
  type EventCategory,
  EventCategories,
} from '@stripe-internal/mkt-analytics';
import {
  useInternalAnalyticsClient,
  useTrackVitals,
  useScrollDepthTracker,
} from '@stripe-internal/nautilus-components';

const ALLOWED_CATEGORIES: EventCategory[] = [
  'Buttons',
  'Links',
  'Navigation',
  'PDFs',
  'Countries',
  'Languages',
  'Filters',
  'ActionResult',
  'Footer',
  'Modals',
  'Carousels',
];

// Generate an analytics slug for the given sentence-form label
// (e.g. "Flexible solutions for your business" -> "flexible_solutions_for")
export const slugifyLabel = (
  label: string,
  {maxWords = 3}: {maxWords?: number} = {},
): string => {
  return label.toLowerCase().split(' ').slice(0, maxWords).join('_');
};

const isValidCategory = (category: string): category is EventCategory =>
  EventCategories.some((validCategory) => validCategory === category);

const isAllowedCategory = (category: EventCategory): boolean =>
  ALLOWED_CATEGORIES.includes(category);

function inferCategory(element: HTMLElement): EventCategory {
  const tagName = element.tagName.toLowerCase();
  const role = element.getAttribute('role');

  if (tagName === 'a' || role === 'link') {
    return 'Links';
  }

  if (tagName === 'button') {
    return 'Buttons';
  }

  if (tagName === 'nav' || element.closest('nav')) {
    return 'Navigation';
  }

  const href = element.getAttribute('href');
  if (href && href.endsWith('.pdf')) {
    return 'PDFs';
  }

  return 'Buttons';
}

function getAction(element: HTMLElement): string {
  const explicitAction = element.dataset.analyticsAction;
  if (explicitAction && explicitAction.length > 0) {
    return explicitAction;
  }

  return 'Clicked';
}

function getCategory(element: HTMLElement): EventCategory {
  const maybeCategory = element.dataset.analyticsCategory;

  if (!maybeCategory || maybeCategory.length === 0) {
    return inferCategory(element);
  }

  if (!isValidCategory(maybeCategory) || !isAllowedCategory(maybeCategory)) {
    return inferCategory(element);
  }

  return maybeCategory;
}

/**
 * Reports page analytics and sets up global tracking.
 *
 * This hook performs four main functions:
 * 1. Tracks a page view event on mount using the page metadata
 * 2. Tracks web vitals
 * 3. Tracks scroll depth
 * 4. Sets up a global click handler that tracks analytics events for elements
 *    with `data-analytics-label` attributes
 *
 * The click handler supports the following data attributes:
 * - `data-analytics-label` (required): The label for the event
 * - `data-analytics-category` (optional): Event category (e.g., 'Buttons', 'Links')
 * - `data-analytics-action` (optional): Event action (defaults to 'Clicked')
 * - `data-analytics-value` (optional): Numeric value associated with the event
 * - `data-analytics-description` (optional): Additional event description
 *
 * If no category is provided, it will be inferred from the element type.
 */
export function useReportPageAnalytics() {
  const {analytics: analyticsMetadata} = usePageMetadata();
  const analyticsClient = useInternalAnalyticsClient();

  // Track page view event on mount
  useEffect(() => {
    analyticsClient.trackPage({
      id: analyticsMetadata.id,
      title: analyticsMetadata.title,
      path: location.pathname,
      path_with_qs: location.pathname + location.search,
      referrer: document.referrer,
    });
  }, [analyticsClient, analyticsMetadata]);

  // Track web vitals
  useTrackVitals({fps: true, ttfb: true});

  // Track scroll depth
  useScrollDepthTracker({
    trackPercentages: true,
    percentages: [10, 25, 50, 75, 90],
    flushInterval: 1000,
  });

  // Set up global CTA click handler
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const targetEl = event.target instanceof Element ? event.target : null;
      if (!targetEl) return;

      const analyticsElement = targetEl.closest(
        '[data-analytics-label]',
      ) as HTMLElement | null;
      if (!analyticsElement) return;

      const label = analyticsElement.dataset.analyticsLabel;
      if (!label || label.length === 0) return;

      const category = getCategory(analyticsElement);
      const action = getAction(analyticsElement);
      const value = analyticsElement.dataset.analyticsValue;
      const description = analyticsElement.dataset.analyticsDescription;

      let destinationUrl: string | undefined;
      if (analyticsElement instanceof HTMLAnchorElement) {
        destinationUrl = analyticsElement.href;
      }

      analyticsClient.trackEvent({
        category,
        action,
        label,
        value: value ? Number(value) : undefined,
        description,
        ...(destinationUrl ? {destination_url: destinationUrl} : {}),
      });
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [analyticsClient]);
}
